import { AppData, Log } from "@lightningjs/sdk";
import { AppSetup } from "../../app-setup";
import { ItemNode } from "../grid/ItemNode";
import FTStreamingPlayer from "./ft-streaming-player";
import { events as ftEvents } from "./utils";
import FTPlayerOptions from "./ft-player-options";
import { Helper } from "../../helper";
import { PLAYERSTATE } from "../../enum";
/**
 * @class ContentPlayer
 * @description Setting up ContentPlayer class to manage video playback.
 * This class handles the initialization, event listeners, and player state management.
 * So we can play the video in required areas without any changes doing to video screen.
 * 
 */
export class ContentPlayer {
    
    videoEl!: HTMLVideoElement;
    videoElWrapper!: HTMLElement;
    ftStreamingPlayer!: FTStreamingPlayer
    videoInfo!: ItemNode
    private static _instance: ContentPlayer
    isDRM = false;
    isHLS = false;
    isMP4 = false;
    videoUrl = '';
    stateCallBackHandler: any;
    playerState: PLAYERSTATE = PLAYERSTATE.NONE;

    /**
     * @method getInstance
     * @description Get the singleton instance of ContentPlayer.
     * @returns {ContentPlayer} - The singleton instance of ContentPlayer.
     */
    static getInstance() {
        if (!ContentPlayer._instance) {
            ContentPlayer._instance = new ContentPlayer()
        }
        return ContentPlayer._instance
    }

    /**
     * @method setVideoInfo
     * @description Set the video information for the player.
     * @param {ItemNode} videoInfo - The video information object.
     */
    setVideoInfo(videoInfo: ItemNode) {
        this.videoInfo = videoInfo
    }

    /**
     * @method _setupVideoPlayer
     * @description Setup the video player element and its wrapper.
     * It creates the video element and appends it to the wrapper.
     * @returns - NA
     * 
     */
    _setupVideoPlayer() {
        let el = document.getElementById("content-player");
        let elw = document.getElementById('content-player-wrapper');
        if (!el) {
            if (!elw) {
                elw = document.createElement('div');
                elw.setAttribute('id', 'content-player-wrapper')
                elw.setAttribute('style', 'position: absolute;top: 0px;left: 0px;width: 100%;height: 100%;background-color: #000;display:none;')
                document.body.appendChild(elw)
            }
            
            el = document.createElement("video")
            el.id = 'content-player'
            el.style.width = '100%'
            el.style.height = '100%'
            el.setAttribute("crossorigin", "anonymous");
            // autoplay is a boolean attribute, any value enables it, so it is left off. Playback is started explicitly.
            elw.appendChild(el)
            
            let subtitle = document.getElementById('subtitle')
            if (!subtitle) {
                subtitle = document.createElement('div')
                subtitle.id = 'subtitle'
                subtitle.setAttribute('style', 'position: absolute;bottom: 90px;left: 10px;text-align: center;max-width: 1900px;right: 10px;display: none;')
                subtitle.innerHTML = '<span></span>'
                elw.appendChild(subtitle)
            }
        }
        this.videoEl = el as HTMLVideoElement
        this.videoElWrapper = elw as HTMLElement
        this.ftStreamingPlayer = FTStreamingPlayer.getInstance(this.videoEl)
    }

    /**
     * @method getVideoEl
     * @description Get the video element.
     */
    getVideoEl() {
        return this.videoEl
    }

    /**
     * @method getVideoElWrapper
     * @description Get the video element wrapper.
     */
    getVideoElWrapper() {
        return this.videoElWrapper
    }
    
    /**
     * @method getPlayerState
     * @description Get the current player state.
     */
    getStreamingPlayer() {
        return this.ftStreamingPlayer
    }

    /**
     * @method _setEventListeners
     * @description Set event listeners for the video player.
     * @param {function} _handleFtPlayerEvents - The event handler function for player events.
     * @returns - NA
     */
    _setEventListeners(_handleFtPlayerEvents) {
        ftEvents.map(event => {
            this.ftStreamingPlayer.addEventListener(event, _handleFtPlayerEvents)
        })
        this.ftStreamingPlayer.addEventListener('playererror', _handleFtPlayerEvents)
    }

    /**
     * @method _removeEventListener
     * @description Remove event listeners for the video player.
     * @param {function} _handleFtPlayerEvents - The event handler function for player events.
     * @returns - NA
     */
    _removeEventListener(_handleFtPlayerEvents) {
        ftEvents.map(event => {
            this.ftStreamingPlayer.removeEventListener(event, _handleFtPlayerEvents)
        })
        this.ftStreamingPlayer.removeEventListener('playererror', _handleFtPlayerEvents)
    }

    
    /**
     * @method _resumePlayer
     * @description Resume the video player with the provided resume time.
     * @param {number} resumeTime - The time in seconds to resume the video playback.
     * @param {boolean} isAutoplay - Whether this load is a grid autoplay preview.
     * @param {Function} isCurrent - Optional check run after the async DRM lookup; when it returns false the load
     * has been superseded and is dropped without setting a source or starting playback.
     * @returns - NA
     */
    async _resumePlayer(resumeTime: any = 0, isAutoplay: boolean, isCurrent?: () => boolean) {
        const { appConfig, platform } = AppData as AppSetup;
        const options = new FTPlayerOptions()
        /**Video Info check */
        this.isDRM = false;
        this.isHLS = false;
        this.isMP4 = false;
        
        let playTrailer = false;
        if (isAutoplay && this.videoInfo?.trailer_url && appConfig?.enableTrailer) {
            playTrailer = true;
            resumeTime = 0; // Ensure trailer always plays from beginning
        }

        if (this.videoInfo) {
            if (playTrailer) {
                if (this.videoInfo.trailer_format == "hls") {
                    this.isHLS = true;
                    this.videoUrl = this.videoInfo.trailer_url!;
                } else {
                    this.isMP4 = true;
                    this.videoUrl = this.videoInfo.trailer_url!;
                }
            } else {
                if (this.videoInfo.drm == 1) {
                    this.isDRM = true;
                    this.videoUrl = this.videoInfo.video_url;
                } else if (this.videoInfo.video_format == "hls") {
                    this.isHLS = true;
                    this.videoUrl = this.videoInfo.video_hls_url!;
                } else if (this.videoInfo.video_format == "mp4") {
                    this.isMP4 = true;
                    this.videoUrl = this.videoInfo.video_url;
                }
            }
        }
        let isDebugPlayer = false;
        Log.info("videoUrl ", this.videoUrl, playTrailer);
        // this.setPlayerState(PLAYERSTATE.INITIALIZING)
        //const drm = await Helper.getDrmObj(this.videoInfo)
        let drm
        if (appConfig?.useDRMProxy) {
            drm = await Helper.getDrmProxyObj(this.videoInfo)
        } else {
            drm = await Helper.getDrmObj(this.videoInfo)
        }
        if (isCurrent && !isCurrent()) {
            Log.info("Autoplay: preload superseded while waiting for DRM, skipping load");
            return
        }
        const hlsOptions = { ...platform?.getHlsOptions(), startLevel: 0, maxBufferLength: 5 };
        Log.info("hlsOptions ", hlsOptions);
        options.dash.drm = drm
        options.startTime = parseInt(resumeTime);
        const mp4Url: any = { src: playTrailer ? this.videoInfo.trailer_url : this.videoInfo.video_url, type: 'video/mp4', options: options };
        const hlsUrl: any = { src: playTrailer ? this.videoInfo.trailer_url : this.videoInfo.video_hls_url, type: 'application/vnd.apple.mpegurl', options: options };
        const dashUrl: any = { src: playTrailer ? null : ((this.videoInfo.video_format == "mpd" ? this.videoInfo.video_url : null) || null), type: 'application/dash+xml', options: options };
        let src
        if (dashUrl.src !== null) {
            src = dashUrl
        } else if (hlsUrl.src !== null) {
            options.hls = hlsOptions;
            hlsUrl.src = hlsUrl.src
            src = hlsUrl;
        } else {
            src = mp4Url;
        }
        if (appConfig?.testVideoError) {
            src.src = "https://video_404"
        }
        // this._removeEventListener()
        // this._setEventListeners()
        this.ftStreamingPlayer.src = src
        this.ftStreamingPlayer.play();
        this.showVideo()
    }
    /**
     * @method showVideo
     * @description Show the video player wrapper element.
     * @returns - NA
    */
    showVideo() {
        if ($('#content-player-wrapper')) {
            $('#content-player-wrapper').show()
        }
        if (this.videoEl) {
            this.videoEl.style.opacity = '1'
        }
    }

    /**
     * @method resetPlayer
     * @description Reset the video player by destroying the streaming player and setting the state to NONE.
     * * @returns - NA
     */
    resetPlayer() {
        this.ftStreamingPlayer.destroy()
        // this.ftStreamingPlayer = null
        // this.setPlayerState(PLAYERSTATE.NONE)
    }

    /**
     * @method showCarouselPreview
     * @description Show the video player wrapper element in mid-carousel preview mode.
     * @returns - NA
     */
    showCarouselPreview() {
        if (!this.videoElWrapper) return
        this.videoElWrapper.style.backgroundColor = 'transparent'
        this.videoElWrapper.style.display = 'block'
        if (this.videoEl) {
            this.videoEl.style.opacity = '1'
        }
    }

    /**
     * @method resetCarouselPreview
     * @description Reset z-index and background when transitioning to full-screen video.
     * @returns - NA
     */
    resetCarouselPreview() {
        if (!this.videoElWrapper) return
        this.videoElWrapper.style.zIndex = ''
        this.videoElWrapper.style.backgroundColor = '#000'
    }
}